import { useMemo } from 'react';

import { useTheme } from '@mui/material';
import { Pie as RechartsPie, PieSectorShapeProps } from 'recharts';

import { defaults } from './_constants';
import {
  calculateInnerRadius,
  calculateSectorWidth,
  getIsFullCircle,
  hasValue,
  modifyDataStyle,
} from './_utils';
import { usePieChartContext } from './PieChartContext';
import { PieShape } from './PieShape';
import { PieChartData, PieProps } from './types';
import { mapPaletteColorToDataColorToken } from '../../../../utils/mapPaletteColorToDataColorToken';
import { getThemeColor } from '../../../../utils';
import { useChartConfig } from '../../ChartConfigProvider';

/**
 * Pie chart component.
 */
export const Pie = <T extends PieChartData>({
  data: dataProp,
  dataKey: dataKeyProp,
  fill = defaults.fill,
  stroke,
  startAngle: startAngleProp,
  endAngle: endAngleProp,
  isAnimationActive,
}: PieProps<T>) => {
  const theme = useTheme();
  const themedFill = getThemeColor(
    theme,
    mapPaletteColorToDataColorToken(fill, fill)
  );
  const themedStroke = getThemeColor(
    theme,
    stroke ? mapPaletteColorToDataColorToken(stroke, stroke) : stroke
  );

  const {
    data: containerData,
    radius,
    startAngle: startAngleContext,
    endAngle: endAngleContext,
  } = usePieChartContext<T>();
  const chartConfig = useChartConfig({ isAnimationActive });

  const { data, dataKey } = useMemo(() => {
    const styledData = (dataProp ?? containerData)
      ?.map((point) => {
        return {
          ...point,
          style: modifyDataStyle(theme, point, !!themedStroke),
        };
      })
      ?.filter((point) => {
        // Filter out zero-value sectors as they cause issues with the pie shape
        return hasValue(point, dataKeyProp);
      });
    if (!styledData?.length) {
      // Empty sector to fill the rest of the circle
      return { data: [{ value: 1 }], dataKey: 'value' };
    }
    return { data: styledData, dataKey: dataKeyProp };
  }, [dataProp, dataKeyProp, themedStroke, containerData, theme]);

  const sectorGap = defaults.sectorGap;
  const sectorWidth = calculateSectorWidth(radius);
  const innerRadius = calculateInnerRadius({ radius, sectorGap, sectorWidth });
  const cornerRadius = defaults.cornerRadius;

  const startAngle = startAngleProp ?? startAngleContext;
  const endAngle = endAngleProp ?? endAngleContext;

  const shape = (sectorProps: PieSectorShapeProps) => (
    <PieShape
      {...sectorProps}
      gapWidth={sectorGap}
      dataLength={data?.length ?? 0}
      isFullCircle={getIsFullCircle({ startAngle, endAngle })}
    />
  );

  return (
    <RechartsPie
      data={data}
      dataKey={dataKey}
      startAngle={startAngle}
      endAngle={endAngle}
      cornerRadius={cornerRadius}
      outerRadius={defaults.outerRadius}
      innerRadius={innerRadius}
      fill={themedFill}
      stroke={themedStroke ?? themedFill}
      strokeWidth={defaults.strokeWidth}
      shape={shape}
      isAnimationActive={chartConfig.isAnimationActive}
      animationBegin={chartConfig.isAnimationActive ? 200 : 0}
    />
  );
};
